import React from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Link } from 'react-router-dom';

const Contact = () => {
    const { siteContent, currentUser, addContactMessage } = useApp();
    const { contact } = siteContent;
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [isSent, setIsSent] = React.useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const form = e.target;
        const msg = {
            firstName: form.elements[0].value,
            lastName: form.elements[1].value,
            email: form.elements[2].value,
            message: form.elements[3].value
        };

        // Simulated API Delay
        setTimeout(() => {
            if (addContactMessage) addContactMessage(msg);
            setIsSubmitting(false);
            setIsSent(true);
            form.reset();
            setTimeout(() => setIsSent(false), 5000);
        }, 1200);
    };

    return (
        <div className="pt-20 pb-16 min-h-screen bg-gray-50 relative">


            {/* Header */}
            <div className="bg-primary-900 text-white py-16 text-center">
                <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
                <p className="text-primary-100">We'd love to hear from you. Reach out to us for any queries.</p>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
                <div className="grid md:grid-cols-3 gap-8">
                    {/* Contact Cards */}
                    <div className="bg-white p-8 rounded-xl shadow-lg text-center transform hover:-translate-y-1 transition-transform">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
                            <Phone className="w-6 h-6" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">Phone</h3>
                        <p className="text-gray-600">{contact.phone}</p>
                        <p className="text-xs text-gray-500 mt-2">{contact.officeHours}</p>
                    </div>

                    <div className="bg-white p-8 rounded-xl shadow-lg text-center transform hover:-translate-y-1 transition-transform">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
                            <Mail className="w-6 h-6" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">Email</h3>
                        <p className="text-gray-600">{contact.email}</p>
                        <p className="text-xs text-gray-500 mt-2">{contact.emailSubtitle}</p>
                    </div>

                    <div className="bg-white p-8 rounded-xl shadow-lg text-center transform hover:-translate-y-1 transition-transform border-b-4 border-green-500">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                            <Phone className="w-6 h-6" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">WhatsApp</h3>
                        <p className="text-gray-600">9468097665</p>
                        <a
                            href="https://wa.me/919468097665?text=Hi%20Admin,%20I%20need%20some%20support."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block mt-4 text-sm font-bold text-green-600 hover:text-green-700 underline"
                        >
                            Chat on WhatsApp
                        </a>
                    </div>

                    <div className="bg-white p-8 rounded-xl shadow-lg text-center transform hover:-translate-y-1 transition-transform">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
                            <MapPin className="w-6 h-6" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">Office</h3>
                        <p className="text-gray-600">{contact.address}</p>
                    </div>
                </div>

                {/* Form map section */}
                <div className="mt-12 grid md:grid-cols-2 gap-8">
                    <div className="bg-white p-8 rounded-2xl shadow-sm">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
                        {isSent && (
                            <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg border border-green-100 flex items-center animate-in fade-in slide-in-from-top-2">
                                <span className="font-bold mr-2">Success!</span> We have received your message.
                            </div>
                        )}
                        <form className="space-y-4" onSubmit={handleSubmit}>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                    <input required type="text" className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none" placeholder="John" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                    <input required type="text" className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none" placeholder="Doe" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                <input required type="email" className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none" placeholder="john@example.com" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                <textarea required rows="4" className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none" placeholder="How can we help you?"></textarea>
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {isSubmitting ? 'Sending...' : (
                                    <>
                                        <Send className="w-4 h-4 mr-2" />
                                        Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    <div className="bg-gray-200 rounded-2xl overflow-hidden min-h-[400px]">
                        {/* Placeholder for Map */}
                        <div className="w-full h-full flex items-center justify-center text-gray-500 bg-gray-100">
                            Google Maps Integration would go here
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
